import React, { useEffect, useMemo, useState } from 'react';
import SEO from '../../components/SEO';
import PageLoader from '../../components/PageLoader';
import api from '../../utils/api';
import AdminLayout from '../../components/admin/AdminLayout';
import AdminListToolbar from '../../components/admin/AdminListToolbar';
import Pagination from '../../components/Pagination';
import { useSidebar } from '../../contexts/SidebarContext';
import { showSuccess, showError, showConfirm } from '../../utils/toast';

const AdminCitiesContent = () => {
  const { isCollapsed } = useSidebar();
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCity, setEditingCity] = useState(null);
  const [formData, setFormData] = useState({ name: '', domain: '', active: true });
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('a-z');
  const [limit, setLimit] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    fetchCities();
  }, []);

  const fetchCities = async () => {
    try {
      const response = await api.get('/cities');
      setCities(response.data);
    } catch (error) {
      console.error('Error fetching cities:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredAndSorted = useMemo(() => {
    let list = cities;
    const s = (search || '').toLowerCase().trim();
    if (s) {
      list = list.filter((c) =>
        (c.name || '').toLowerCase().includes(s) ||
        (c.domain || '').toLowerCase().includes(s) ||
        (c.slug || '').toLowerCase().includes(s)
      );
    }
    list = [...list].sort((a, b) => {
      const na = (a.name || '').toLowerCase();
      const nb = (b.name || '').toLowerCase();
      return sort === 'z-a' ? nb.localeCompare(na) : na.localeCompare(nb);
    });
    return list;
  }, [cities, search, sort]);

  const totalItems = filteredAndSorted.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / limit));
  const paginatedList = useMemo(
    () => filteredAndSorted.slice((currentPage - 1) * limit, currentPage * limit),
    [filteredAndSorted, currentPage, limit]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCity) {
        await api.put(`/cities/${editingCity._id}`, formData);
      } else {
        await api.post('/cities', formData);
      }
      setShowModal(false);
      setEditingCity(null);
      setFormData({ name: '', domain: '', active: true });
      fetchCities();
      showSuccess(editingCity ? 'City updated successfully!' : 'City created successfully!');
    } catch (error) {
      showError(error.response?.data?.message || 'Error saving city');
    }
  };

  const handleEdit = (city) => {
    setEditingCity(city);
    setFormData({ name: city.name, domain: city.domain, active: city.active });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    showConfirm(
      'Are you sure you want to delete this city? This action cannot be undone.',
      async () => {
        try {
          await api.delete(`/cities/${id}`);
          fetchCities();
          showSuccess('City deleted successfully!');
        } catch (error) {
          showError(error.response?.data?.message || 'Error deleting city');
        }
      }
    );
  };

  return (
    <>
      <SEO title="Manage Cities - Admin" description="Manage cities." noindex />

        <main className={`flex-1 transition-all duration-300 ${isCollapsed ? 'ml-16' : 'ml-64'}`}>
          <div className="container-custom py-6 md:py-12">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 md:mb-8">
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 dark:text-gray-50">Manage Cities</h1>
          <button
            onClick={() => {
              setEditingCity(null);
              setFormData({ name: '', domain: '', active: true });
              setShowModal(true);
            }}
            className="btn-primary text-sm whitespace-nowrap"
          >
            <span className="hidden sm:inline">Add City</span>
            <span className="sm:hidden">+ Add</span>
          </button>
        </div>

        {!loading && (
          <AdminListToolbar
            searchInput={searchInput}
            onSearchInputChange={setSearchInput}
            search={search}
            onSearch={() => { setSearch(searchInput); setCurrentPage(1); }}
            onClear={() => { setSearchInput(''); setSearch(''); setCurrentPage(1); }}
            sort={sort}
            onSortChange={(v) => { setSort(v); setCurrentPage(1); }}
            limit={limit}
            onLimitChange={(v) => { setLimit(v); setCurrentPage(1); }}
            searchPlaceholder="Search by name, domain, or slug..."
          />
        )}

        {loading ? (
          <PageLoader message="Loading cities..." />
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="hidden lg:block card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase w-14">Sr. No.</th>
                      <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Name</th>
                      <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Domain</th>
                      <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Slug</th>
                      <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                      <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                    {paginatedList.map((city, index) => {
                      const srNo = (currentPage - 1) * limit + index + 1;
                      return (
                      <tr key={city._id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                        <td className="px-4 md:px-6 py-4 whitespace-nowrap text-gray-600 dark:text-gray-400 text-sm">{srNo}</td>
                        <td className="px-4 md:px-6 py-4 whitespace-nowrap font-semibold text-gray-900 dark:text-gray-50">{city.name}</td>
                        <td className="px-4 md:px-6 py-4 whitespace-nowrap font-mono text-sm text-gray-700 dark:text-gray-300">{city.domain}</td>
                        <td className="px-4 md:px-6 py-4 whitespace-nowrap text-gray-500 dark:text-gray-400 text-sm">{city.slug}</td>
                        <td className="px-4 md:px-6 py-4 whitespace-nowrap">
                          <span className={`badge text-xs ${city.active ? 'badge-success' : 'badge-error'}`}>
                            {city.active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-4 md:px-6 py-4 whitespace-nowrap">
                          <div className="flex gap-3">
                            <button
                              onClick={() => handleEdit(city)}
                              className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm font-medium transition-colors"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDelete(city._id)}
                              className="text-error-600 dark:text-error-400 hover:text-error-700 dark:hover:text-error-300 text-sm font-medium transition-colors"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );})}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mobile Card View */}
            <div className="lg:hidden space-y-4">
              {paginatedList.map((city, index) => {
                const srNo = (currentPage - 1) * limit + index + 1;
                return (
                <div key={city._id} className="card p-4 md:p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-medium text-gray-500 dark:text-gray-400">{srNo}.</span>
                        <h3 className="font-bold text-lg text-gray-900 dark:text-gray-50">{city.name}</h3>
                      </div>
                      <p className="text-sm text-gray-500 dark:text-gray-400 font-mono mb-2">{city.domain}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">{city.slug}</p>
                      <span className={`badge text-xs ${city.active ? 'badge-success' : 'badge-error'}`}>
                        {city.active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2 pt-3 border-t border-gray-200 dark:border-gray-700">
                    <button
                      onClick={() => handleEdit(city)}
                      className="btn-outline text-sm py-1.5 px-3 flex-1"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(city._id)}
                      className="btn-outline text-sm py-1.5 px-3 flex-1 text-error-600 dark:text-error-400 border-error-600 dark:border-error-400"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              );})}
            </div>

            {paginatedList.length === 0 && (
              <div className="card p-12 text-center">
                <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <p className="text-gray-600 dark:text-gray-400 text-lg">{search ? 'No cities match your search.' : 'No cities found.'}</p>
              </div>
            )}

            {totalPages >= 1 && (
              <div className="mt-6">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={totalItems}
                  itemsPerPage={limit}
                  itemsOnCurrentPage={paginatedList.length}
                  onPageChange={(p) => { setCurrentPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                />
              </div>
            )}
          </>
        )}

        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50 p-4 animate-fade-in">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 md:p-8 w-full max-w-md animate-scale-in">
              <h2 className="text-xl md:text-2xl font-bold mb-4 text-gray-800 dark:text-gray-50">
                {editingCity ? 'Edit City' : 'Add City'}
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-2">Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="input-field"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-2">Domain (Subdomain) *</label>
                  <input
                    type="text"
                    value={formData.domain}
                    onChange={(e) => setFormData({ ...formData, domain: e.target.value.toLowerCase() })}
                    required
                    placeholder="fatehabad"
                    className="input-field"
                  />
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    This will be used as subdomain (e.g., {formData.domain || 'fatehabad'}.idbf.in)
                  </p>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.active}
                    onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <label className="ml-2 text-gray-700 dark:text-gray-300">Active</label>
                </div>
                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <button
                    type="submit"
                    className="btn-primary flex-1"
                  >
                    {editingCity ? 'Update' : 'Create'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingCity(null);
                    }}
                    className="btn-secondary flex-1"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
          </div>
        </main>
    </>
  );
};

const AdminCities = () => {
  return (
    <AdminLayout>
      <AdminCitiesContent />
    </AdminLayout>
  );
};

export default AdminCities;

